import { Injectable } from '@angular/core';
import { ValidationResult, Validation } from '../../interfaces/validation/ValidationInterface';
import { Utils } from '../../../../common/js/Utils';
import { WindowService } from '../../../../common/angular-shared/services/window/window.service';

import _ from 'lodash';
import { Product, RiderConstants, RiderType, getWLProductCode } from '../../../../common/js/static/Product';
import {
  Exchange1035FundingPurpose,
  GiactWebDebitResponse,
  ProductQuoteResponse,
  WholeLifeQuotePremiumBreakdown,
} from '../../../js/interfaces/CoverpathTypes';
import { Policy } from '../../../../common/js/customer/Policy';
import { CoverpathPolicy } from '../../../classes/coverpath_subclasses/CoverpathPolicy';
import { CoverpathBasePolicy } from '../../../classes/coverpath_subclasses/CoverpathBasePolicy';
import { CoverpathUtils } from '../../../js/static/CoverpathUtils';
import { RateClasses } from '../../../../common/js/static/RateClasses';
import { CoverpathCustomer } from '../../../classes/coverpath_subclasses/CoverpathCustomer';
import { ClientBaseQuote } from '../../../classes/quote/ClientBaseQuote';
import { PremiumRequest, ProductServiceUtils, Rider } from '../../../../common/js/productService/ProductServiceUtils';
import {
  TermLifeValidationUtils,
  WholeLifeValidationUtils,
  JointAppValidationUtils,
  CIRValidationUtils,
} from '@hit/validation-utils';
import { CommonUtils } from '../../../../common/js/static/CommonUtils';
import { ProductType } from '../../../../common/js/interfaces/CommonTypes';
import { Person } from '../../../../common/js/customer/Person';
import { UnsupportedCase } from '../../classes/UnsupportedUseCases';
import { PremiumCalculationService } from './premium-calculation.service';
import { CasApplication } from '../../../classes/coverpath_subclasses/CasApplication';
import { GiactUtils } from '../../../../common/js/GiactUtils';
import { ValidationUtils } from '../../../js/static/ValidationUtils';
import { ValidationStatic } from './validation-static';
import {
  DIVIDEND_OPTION,
  HEALTH_CLASS,
  TERM_PRODUCT_CODE,
  WHOLE_LIFE_PRODUCT_CODE,
} from '../../../../common/js/constants/policy';

@Injectable()
export class ValidationService {
  rules: Array<Validation>;
  static RESULT: string = 'result';
  static ERR_MSG: string = 'error';
  static UNSUPPORTED_CASE: string = 'unsupportedCase';
  static WARNING_MSG: string = 'warning';
  static UNSUPPORTED_CASE_DATA: string = 'unsupportedCaseData';
  static windowService: WindowService;

  static readonly PROXY_ID_MIN_LEN: number = 4;
  static readonly PROXY_ID_MAX_LEN: number = 6;

  constructor(protected windowService: WindowService) {
    this.rules = [];
    this.rules.push({ validator: 'DOB', validatorFunc: ValidationService.isValidDOB });
    this.rules.push({ validator: 'SSN', validatorFunc: ValidationService.isBasicSSN });
    this.rules.push({ validator: 'TIN', validatorFunc: ValidationService.isBasicTIN });
    this.rules.push({ validator: 'isValidId', validatorFunc: ValidationService.isValidIDNumber });
    this.rules.push({ validator: 'date', validatorFunc: ValidationService.isRegularDate });
    this.rules.push({ validator: 'currencyZero', validatorFunc: ValidationService.isNumeric });
    this.rules.push({ validator: 'currencyRounded', validatorFunc: ValidationService.isNumeric });
    this.rules.push({ validator: 'currencyRoundedConditionally', validatorFunc: ValidationService.isNumeric });
    this.rules.push({ validator: 'currencyWithCents', validatorFunc: ValidationService.isNumeric });
    this.rules.push({ validator: 'email', validatorFunc: ValidationService.isEmail });
    this.rules.push({ validator: 'futuredate', validatorFunc: ValidationService.isFutureDate });
    this.rules.push({ validator: 'pastDate', validatorFunc: ValidationService.isPastDate });
    this.rules.push({ validator: 'geoName', validatorFunc: ValidationService.isGeoName });
    this.rules.push({ validator: 'isMaxLength', validatorFunc: ValidationService.isMaxLength });
    this.rules.push({ validator: 'isMinLength', validatorFunc: ValidationService.isMinLength });
    this.rules.push({ validator: 'name', validatorFunc: ValidationService.isName });
    this.rules.push({ validator: 'numeric', validatorFunc: ValidationService.isNumeric });
    this.rules.push({ validator: 'numericstring', validatorFunc: ValidationService.isNumeric });
    this.rules.push({ validator: 'numericPositive', validatorFunc: ValidationService.isPositiveNumber });
    this.rules.push({ validator: 'numericWhole', validatorFunc: ValidationService.isWholeNumber });
    this.rules.push({ validator: 'required', validatorFunc: ValidationService.notEmpty });
    this.rules.push({ validator: 'shortdate', validatorFunc: ValidationService.isShortDate });
    this.rules.push({ validator: 'telephone', validatorFunc: ValidationService.isTelephone });
    this.rules.push({ validator: 'text', validatorFunc: ValidationService.isText });
    this.rules.push({ validator: 'currency', validatorFunc: ValidationService.isNumeric });
    this.rules.push({ validator: 'zipcode', validatorFunc: ValidationService.isZip });
    this.rules.push({ validator: 'nameSupportComma', validatorFunc: ValidationService.isNameSupportComma });
    this.rules.push({ validator: 'EINSSN', validatorFunc: ValidationService.isEINSSN });
    this.rules.push({ validator: 'numericInt', validatorFunc: ValidationService.isNumeric });
    this.rules.push({ validator: 'amountNonZero', validatorFunc: ValidationService.amountNonZero });
    this.rules.push({ validator: 'password', validatorFunc: ValidationService.isPassword });
    this.rules.push({ validator: 'isLegalAdult', validatorFunc: ValidationService.isLegalAdult });
    this.rules.push({ validator: 'isTobaccoClass', validatorFunc: ValidationService.isTobaccoClass });
    this.rules.push({ validator: 'isNonTobaccoClass', validatorFunc: ValidationService.isNonTobaccoClass });
    this.rules.push({ validator: 'inList', validatorFunc: ValidationService.inList });
    this.rules.push({ validator: 'isTrue', validatorFunc: ValidationService.isTrue });
    this.rules.push({ validator: 'isFalse', validatorFunc: ValidationService.isFalse });
    this.rules.push({ validator: 'tfaMobilePhone', validatorFunc: ValidationService.isTFAMobilePhone });
    this.rules.push({ validator: 'replacements', validatorFunc: ValidationService.replacements });
    this.rules.push({ validator: 'proxyId', validatorFunc: ValidationService.isProxyId });
    this.rules.push({ validator: 'futureshortdate', validatorFunc: ValidationService.isFutureShortDate });
    this.rules.push({ validator: 'isValidGirAmount', validatorFunc: ValidationService.isValidGirAmount });
    this.rules.push({ validator: 'isValidRtrAmount', validatorFunc: ValidationService.isValidRtrAmount });
    this.rules.push({ validator: 'validPastYear', validatorFunc: ValidationService.isValidYearInPast });
    this.rules.push({ validator: 'isValidAlirPlannedYears', validatorFunc: ValidationService.isValidAlirPlannedYears });
    this.rules.push({ validator: 'juviNoPCP', validatorFunc: ValidationService.juviNoPCP });
    this.rules.push({
      validator: 'juviUnknownLastPCPVisitDate',
      validatorFunc: ValidationService.juviUnknownLastPCPVisitDate,
    });
    this.rules.push({
      validator: 'shortDateIsAfterInsuredDOB',
      validatorFunc: ValidationService.shortDateIsAfterInsuredDOB,
    });

    ValidationService.windowService = windowService;
  }

  public runRule(ruleToRun, value, options?): ValidationResult {
    const ruleObj = _.find(this.rules, (rule) => {
      return rule.validator === ruleToRun;
    });

    // replacements validation logic doesn't apply for 2019 filing since the nature and format of questions have changed
    if (
      ruleObj.validator === 'replacements' &&
      (ValidationService.windowService.nativeWindow.controller.customer.isCoverpath2019MetadataEnabled() ||
        !ValidationService.windowService.nativeWindow.controller.enableNewReplacementFeature ||
        this.windowService.nativeWindow.controller.customer.application.primaryPolicy.getChannelCode() !== 'CAS')
    ) {
      return { result: true };
    }

    if (!ruleObj) {
      //tslint:disable-next-line
      console.error(`No Rule found for ${ruleToRun}`);
      return { result: false };
    }

    const validation = ruleObj.validatorFunc(value, options);

    return validation;
  }

  async runValidator(validator, value) {
    return ValidationService.staticRunValidator(validator, value);
  }

  private static async staticRunValidator(validator, value) {
    const args: Array<string> = validator.match(/(.*)\((.*?)\)/);
    if (args) {
      const fnName = args[1].trim();
      const params: Array<any> = args[2].split(/\s*,\s*/);
      params.unshift(value);

      const validatorFunc = ValidationService[fnName];
      if (typeof validatorFunc === 'function') {
        return validatorFunc.apply(this, params);
      }
    }
    //tslint:disable-next-line
    console.error(`No Validator found for ${validator}`);
    return { result: false };
  }

  /**
   * Composite validator runs a validation function that takes multiple parameters.
   * Some of these parameters might be constants or other values from the model
   * that we need in order to validate a field.
   */
  async runCompositeValidator(validationRule: object, dependencies: object, value: number): Promise<ValidationResult> {
    let dependencyMissing: ValidationResult;

    Object.keys(dependencies).forEach((key) => {
      const dependencyValue = dependencies[key];

      if (
        (dependencyValue === undefined || dependencyValue === null) &&
        validationRule['dependencyMissingMessage'][key]
      ) {
        dependencyMissing = { result: false, error: validationRule['dependencyMissingMessage'][key] };
      }
    });

    if (dependencyMissing !== undefined) {
      return dependencyMissing;
    }

    for (const validation of validationRule['validations']) {
      let allDependenciesApply: boolean = true;

      Object.keys(validation.dependencyCheck).forEach(async (dependencyFieldName) => {
        const dependencyFn = validation.dependencyCheck[dependencyFieldName];

        allDependenciesApply =
          allDependenciesApply &&
          (await ValidationService.staticRunValidator(dependencyFn, dependencies[dependencyFieldName])).result;
      });

      if (allDependenciesApply) {
        if (validation.hasOwnProperty('extraValues')) {
          const valueArr = [value];
          validation.extraValues.forEach((extraValue) => {
            const val = dependencies[extraValue];
            valueArr.push(val);
          });

          return ValidationService.staticRunValidator(validation.validate, valueArr);
        }

        return ValidationService.staticRunValidator(validation.validate, value);
      }
    }

    //we've gotten no matches on the dependency checks, so return false
    return { result: false, error: 'The value for this field is not valid' };
  }

  /**
   * Validators.
   * The result of running validation is an object containing
   * if validation was successful, and an error message (if validation failed)
   */

  private static isPassword(value): ValidationResult {
    if (
      value.length < 8 ||
      value.length > 30 ||
      !value.match(/[a-z]/) ||
      !value.match(/[A-Z]/) ||
      !value.match(/[0-9]/) ||
      !value.match(/[?!%$#&+\-_*@]/) ||
      value.match(/[^?!%$#&+\-_*@a-zA-Z0-9]/)
    ) {
      return {
        result: false,
        error:
          'Password must be 8-30 characters with at least one lowercase letter, uppercase letter, number, and special character (? ! % $ # & + - _ * @).',
      };
    }
    return { result: true };
  }

  private static isTrue(value: boolean): ValidationResult {
    if (value) {
      return { result: true };
    } else {
      return { result: false };
    }
  }

  private static isFalse(value: boolean): ValidationResult {
    if (!value) {
      return { result: true };
    } else {
      return { result: false };
    }
  }

  private static isEmpty(value): ValidationResult {
    const isValueEmpty = value === undefined || value === null || value.length === 0;
    return { result: isValueEmpty };
  }

  private static notEmpty(value): ValidationResult {
    const isValueEmpty = value === null || value === undefined || value.length === 0;

    if (isValueEmpty) {
      return { result: false, error: 'Required' };
    }
    return { result: true };
  }

  private static isName(value): ValidationResult {
    const controller = ValidationService.windowService.nativeWindow.controller;
    const error = 'Incorrect format';
    if (controller.nonProdMode) {
      return ValidationService.mustMatch(value, /^[a-z0-9 ,.+'\-]+$/i, error); // non prod mode will allow + to use live flag
    }
    return ValidationService.mustMatch(value, /^[a-z0-9 ,.'\-]+$/i, error);
  }

  private static mustMatch(value: string, regex, error: string): ValidationResult {
    if (value !== null && value.length > 0 && !String(value).match(regex)) {
      return { result: false, error: error };
    }
    return { result: true };
  }

  private static mustNotMatch(value, regex, error): ValidationResult {
    if (value !== null && value.length > 0 && String(value).match(regex)) {
      return { result: false, error: error };
    }
    return { result: true };
  }

  private static isNameSupportComma(value) {
    return ValidationService.mustNotMatch(value, "[^0-9A-Za-z '-,]", 'Please enter a valid name');
  }

  private static isEINSSN(value) {
    return ValidationService.mustMatch(value, /(^\d{2}-?\d{7}$)|(^\d{3}-?\d{2}-?\d{4}$)/, 'Please enter a valid ID');
  }

  private static isValidIDNumber(value) {
    return ValidationService.mustMatch(value, /^[a-zA-Z0-9\-]+[^\W_]$/i, 'Please enter a valid ID');
  }

  private static isDOBWithin(value, min, max): ValidationResult {
    if (value === null || value.length === 0) {
      return null;
    }

    const insuranceAge = ValidationService.getInsuranceAge(value);
    const actualAge = Utils.getActualAge(value);

    // If we are comparing to a minimum age of 18, use the actual age to evaluate
    if (insuranceAge >= max || (parseFloat(min) === 18 ? actualAge < min : insuranceAge < min)) {
      return { result: false, error: `Please enter an age between ${min} and ${max}` };
    }
    return { result: true };
  }

  // Inclusive min, exclusive max
  static isActualAgeWithin(value, inclusiveMin: number, exclusiveMax: number): ValidationResult {
    if (value === null || value.length === 0 || !ValidationService.isDate(value, 'Please enter a valid date')) {
      return null;
    }
    const dob = new Date(value);

    if (CommonUtils.isOlderThanOrEqualToAge(dob, exclusiveMax) || CommonUtils.isYoungerThanAge(dob, inclusiveMin)) {
      return { result: false, error: `Please enter an age between ${inclusiveMin} and ${exclusiveMax - 1}` };
    }
    return { result: true };
  }

  /**
   * This function is called from the 'faceAmount' property validation in MMDirectPolicy
   * It can be called for CoverpathPolicy as well, but currently is not
   *
   * @param policy - policy object (Currently always a MMDirectPolicy, but could be CoverpathPolicy in future)
   */
  private static isTermLifePolicy(policy): ValidationResult {
    if (!policy) {
      return null;
    }
    policy = policy as Policy;
    return { result: policy.isTermLifePolicy() };
  }

  private static getAge(birthdate) {
    //birthdate is stored in UTC, but we want to compare to today in local time for the user
    const today = new Date();
    const ageOnThisYearsBirthday = today.getFullYear() - birthdate.getUTCFullYear();
    const monthDiff = today.getMonth() - birthdate.getUTCMonth();
    const dayDiff = today.getDate() - birthdate.getUTCDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      return ageOnThisYearsBirthday - 1;
    }

    return ageOnThisYearsBirthday;
  }

  public static publicGetAge(birthdate) {
    //birthdate is stored in UTC, but we want to compare to today in local time for the user
    const today = new Date();
    const ageOnThisYearsBirthday = today.getFullYear() - birthdate.getUTCFullYear();
    const monthDiff = today.getMonth() - birthdate.getUTCMonth();
    const dayDiff = today.getDate() - birthdate.getUTCDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      return ageOnThisYearsBirthday - 1;
    }

    return ageOnThisYearsBirthday;
  }

  public static getInsuranceAge(birthdate: Date): number {
    const today = new Date();
    const insuranceDate = Utils.getCurrentMidYearDate(birthdate, today);
    return Utils.yearsBetween(birthdate, today) + (insuranceDate <= today ? 1 : 0);
  }

  private static isLegalAdult(value, min): ValidationResult {
    if (value === null || value.length === 0) {
      return null;
    }

    const age = ValidationService.getAge(value);

    if (age < min) {
      return { result: false, error: 'The owner must be at least 18 years of age' };
    }
    return { result: true };
  }

  private static isTobaccoClass(value): ValidationResult {
    if (value === null || value.length === 0) {
      return { result: false };
    }

    switch (value) {
      case 'SELECT_TOBACCO':
      case 'STANDARD_TOBACCO':
        return { result: true };

      default:
        return { result: false };
    }
  }

  private static isNonTobaccoClass(value): ValidationResult {
    if (value === null || value.length === 0) {
      return { result: false };
    }

    const isTobaccoClass = ValidationService.isTobaccoClass(value);
    return { result: !isTobaccoClass.result };
  }

  private static inList(value: number | string, allowedAsString: string, errorMessage?: string): ValidationResult {
    const allowed = allowedAsString.split('|');

    if (allowed.indexOf(String(value)) === -1) {
      return { result: false, error: errorMessage ? errorMessage : `${value} is not allowed` };
    } else {
      return { result: true };
    }
  }

  private static isTotalWithin(values: number[], min: number, max: number): ValidationResult {
    return ValidationService._isTotalWithin(
      values,
      min,
      max,
      ValidationService.windowService.nativeWindow.controller.lowFaceWholeLifeEnabled
    );
  }
  public static _isTotalWithin(
    values: number[],
    min: number,
    max: number,
    lowFaceWholeLifeEnabled = false
  ): ValidationResult {
    if (Number(min) === 25000) {
      //check if low face whole life feature flag is set
      if (!lowFaceWholeLifeEnabled) {
        //if low face whole life is not enabled, set the minimum to 50k instead.
        min = 50000;
      }
    }
    const total = values.reduce((a, b) => {
      //This checks if term and whole faceamounts are present, both should be over min individually
      if (a > 0 && b > 0 && a < min) {
        return 0;
      } else {
        return a + b;
      }
    }, 0);
    // THIS FUNCTION IS INCLUSIVE!
    if (total === null) {
      return { result: true };
    }

    if (isNaN(total)) {
      return { result: false, error: 'Please enter a number' };
    }
    if (total < min) {
      return { result: false, error: `Please enter a number greater than or equal to ${min}` };
    }

    if (total > max) {
      return {
        result: false,
        error: `Total face amount for this customer cannot exceed ${Utils.formatCurrency(max, 0)}`,
      };
    }
    return { result: true };
  }

  // Similar setup to isTotalWithin to check for lowFaceWholeLifeEnabled flag when evaluating minimum face amount
  private static isWithin(value, min: number, max: number): ValidationResult {
    return ValidationService._isWithin(value, min, max);
  }

  private static _isWithin(value, min: number, max: number): ValidationResult {
    // THIS FUNCTION IS INCLUSIVE!
    if (value === null || value.length === 0) {
      return { result: true };
    }

    const f = parseFloat(value);

    if (isNaN(f)) {
      return { result: false, error: 'Please enter a number' };
    }
    if (f < min) {
      return { result: false, error: `Please enter a number greater than or equal to ${min}` };
    }

    if (f > max) {
      return { result: false, error: `Please enter a number less than or equal to ${max}` };
    }
    return { result: true };
  }

  private static isWithinAge(value, min: number, max: number): ValidationResult {
    if (value === null || value.length === 0) {
      return { result: true };
    }

    const f = parseFloat(value);

    if (isNaN(f)) {
      return { result: false, error: 'Please enter the ages in number' };
    }
    if (f < min || f > max) {
      return { result: false, error: `Please enter a number between the ages of ${min} and ${max}.` };
    }

    return { result: true };
  }

  private static isNumeric(value) {
    return ValidationService.mustNotMatch(value, /[^0-9]/, 'Please enter a number');
  }

  public static amountNonZero(value): ValidationResult {
    if (value === null || value.length === 0) {
      return { result: true };
    }
    if (value <= 0) {
      return { result: false, error: 'Please enter a number greater than zero' };
    }
    return { result: true };
  }

  private static isWholeNumber(value): ValidationResult {
    if (value === null) {
      return { result: true };
    }

    if (!Number.isInteger(value)) {
      return { result: false, error: 'Please enter a whole number' };
    }

    return { result: true };
  }

  private static isPositiveNumber(value): ValidationResult {
    if (value === null || value.length === 0) {
      return { result: true };
    }

    if (!ValidationService.isNumeric(value).result) {
      return { result: false, error: 'Please enter a number' };
    }

    const f = parseFloat(value);

    if (isNaN(f)) {
      return { result: false, error: 'Please enter a number' };
    }

    if (value < 0) {
      return { result: false, error: 'Value cannot be negative' };
    }

    return { result: true };
  }

  private static isNotEmptyAndMinLength(value, len): ValidationResult {
    if (!ValidationService.isEmpty(value).result && value.length < len) {
      return { result: false, error: `Please enter at least ${len} characters` };
    }
    return { result: true };
  }

  private static isNotEmptyAndMaxLength(value, len): ValidationResult {
    if (!ValidationService.isEmpty(value).result && value.length > len) {
      return { result: false, error: `Please enter at most ${len} characters` };
    }
    return { result: true };
  }

  private static isEmail(value) {
    const isValidEmail = Utils.isEmailString(value);

    if (isValidEmail) {
      return { result: true };
    } else {
      return { result: false, error: 'Please enter a valid email address' };
    }
  }

  // short dates are always past dates
  private static isShortDate(value): ValidationResult {
    const isDate = ValidationService.isDate(value, 'Please enter a valid past date mm/yyyy');
    if (isDate.result) {
      return ValidationService.isPastDate(value, true);
    } else {
      return isDate;
    }
  }

  private static isRegularDate(value) {
    return ValidationService.isDate(value, 'Please enter a valid date mm/dd/yyyy');
  }

  private static isValidDOB(value) {
    const errorMessage = 'Please enter a valid date of birth. mm/dd/yyyy';
    if (ValidationService.isDate(value, errorMessage).result) {
      const now = new Date();
      const utcNow = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
      if (utcNow > value && utcNow.getFullYear() - value.getFullYear() <= 100) {
        return { result: true };
      }
    }
    return { result: false, error: errorMessage };
  }

  private static isDate(value, error): ValidationResult {
    if (!ValidationService.isEmpty(value).result && value instanceof Date && !isNaN(value.getTime())) {
      return { result: true };
    } else {
      return { result: false, error: error };
    }
  }

  private static isFutureDate(value): ValidationResult {
    if (!ValidationService.isEmpty(value).result && value instanceof Date) {
      const now = new Date();
      const nowPlus100Years = now.getFullYear() + 100;
      if (now < value && nowPlus100Years >= value.getFullYear()) {
        return { result: true };
      } else {
        return { result: false, error: 'Please enter a valid future date' };
      }
    } else {
      return { result: false, error: 'Please enter a future date mm/dd/yyyy' };
    }
  }

  private static isPastDate(value, shortDate?: boolean): ValidationResult {
    const isDate = ValidationService.isDate(value, 'Please enter a valid past date');
    if (!isDate.result) {
      return isDate;
    }
    if (!value) {
      return null;
    }
    // Has to be a past date within 100 years
    const date = new Date(value),
      now = new Date();
    const nowMinus100 = shortDate
      ? new Date(Date.UTC(now.getFullYear(), now.getMonth())).getFullYear() - 100
      : new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())).getFullYear() - 100;

    if (
      date > new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) ||
      date.getFullYear() < nowMinus100
    ) {
      return { result: false, error: 'Please enter a valid past date' };
    }

    return { result: true };
  }

  private static pastDate(value) {
    return ValidationService.isPastDate(value);
  }

  private static isValidYearInPast(value) {
    const isNumeric = ValidationService.isNumeric(value);
    if (!isNumeric.result) {
      return isNumeric;
    }
    const now = new Date();
    const currentYear = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())).getFullYear();
    const nowMinusValue = currentYear - value;
    if (nowMinusValue > 300 || value > currentYear) {
      return { result: false, error: 'Please enter a valid past year' };
    }
    return { result: true };
  }

  private static isTelephone(value): ValidationResult {
    if (ValidationService.isEmpty(value).result) {
      return { result: true };
    }

    let str;
    let ext;

    if (value.match(/\D/) || value.length < 10) {
      return { result: false, error: 'Please enter a valid phone number' };
    }

    str = value.replace(/[^0-9]/g, '');

    if (str.length > 10) {
      str = str.replace(/^1/, '');
      ext = str.length > 10 ? ' ' + str.substr(10) : '';

      if (ext.length > 11) {
        return { result: false, error: 'Phone number is too long' };
      }
    }

    return { result: true };
  }

  /**
   * Validates a basic SSN, must be of exact format XXX-XX-XXXX,
   * with the correct number of digits, and dashes spaced exactly.
   */
  private static isBasicSSN(value): ValidationResult {
    if (!value.match(/^\d{3}-?\d{2}-?\d{4}$/)) {
      return { result: false, error: 'SSN must be of exact format XXX-XX-XXXX' };
    }

    return { result: true };
  }

  private static isBasicTIN(value) {
    if (!value.match(/^\d{2}-?\d{7}$/)) {
      return { result: false, error: 'TIN must be of exact format XX-XXXXXXX' };
    }

    return { result: true };
  }

  private static isText(value) {
    return ValidationService.mustNotMatch(value, "[^\\-0-9A-Za-z !@#$%^&*()_+={}|][\":;'<>?',.]", 'ERROR: Text');
  }

  private static isGeoName(value) {
    /**
     * Regular Expression translation:
     * Starts with one or more letters or any unicode between 0080 and 024F (inclusive),
     * followed by zero or one dot space(. ), or dash(-), or space ( ), or left/right parenthesis, or apostrophe ('),
     * then ending with zero or more letters or any unicode between 0080 and 024F (inclusive)
     **/
    return ValidationService.mustMatch(
      value,
      /^([a-zA-Z\u0080-\u024F]+(?:. |-| | *\(|\)|'))*[a-zA-Z\u0080-\u024F]*$/,
      'Invalid Name'
    );
  }

  private static isMaxLength(value, len): ValidationResult {
    if (!value || value.length > len) {
      return { result: false, error: 'Please enter at most ' + len + ' characters' };
    }

    return { result: true };
  }

  private static isMinLength(value, len): ValidationResult {
    if (!value || value.length < len) {
      return { result: false, error: 'Please enter at least ' + len + ' characters' };
    }

    return { result: true };
  }

  private static isZip(zipcode): ValidationResult {
    let isValidZipcode = true;

    if (!zipcode.match(/^\d{9}$/) && !zipcode.match(/^\d{5}(?:[-\s]\d{4})?$/)) {
      isValidZipcode = false;
    }

    if (isValidZipcode) {
      return { result: true };
    } else {
      return { result: false, error: 'Please enter a valid Zip code' };
    }
  }

  private static isTFAMobilePhone(value): ValidationResult {
    if (!value || value.match(/\D/) || value.length < 10) {
      return { result: false, error: 'Please enter a valid phone number' };
    }
    return ValidationService.mustMatch(value, /^1?[023456789]\d{9}$/, 'Please enter a valid phone number');
  }

  private static isProxyId(value): ValidationResult {
    const regex = new RegExp(`^\\d{${ValidationService.PROXY_ID_MIN_LEN},${ValidationService.PROXY_ID_MAX_LEN}}$`);
    return regex.test(value) ? { result: true } : { result: false, error: 'Please enter a valid proxy ID' };
  }

  private static isFutureShortDate(value): ValidationResult {
    if (!ValidationService.isEmpty(value).result && value instanceof Date) {
      return ValidationService.isFutureDate(value);
    } else {
      return ValidationService.mustMatch(
        value,
        /^([0]?[1-9]|[1][0-2])[.\/-]([0-9]{4}|[0-9]{2})$/,
        'Please enter a valid date mm/yyyy'
      );
    }
  }

  public static replacementLoanIsZero(value): ValidationResult {
    if (value !== 0) {
      return {
        result: false,
        unsupportedCase: UnsupportedCase.replacementLoanWith1035,
      };
    }
    return {
      result: true,
    };
  }

  private static isWL10(product: string): ValidationResult {
    const isWL10 =
      product === Product.productTypes.Whole.productCodes.CPL10 ||
      product === Product.productTypes.Whole.productCodes.CPL10A;
    return { result: isWL10 };
  }

  private static isWL12Or15Or20(product: string): ValidationResult {
    const isWL10or15or20 =
      product === Product.productTypes.Whole.productCodes.CPL12 ||
      product === Product.productTypes.Whole.productCodes.CPL15 ||
      product === Product.productTypes.Whole.productCodes.CPL20;
    return { result: isWL10or15or20 };
  }

  private static isNotWL10(product: string): ValidationResult {
    const isNotWL10 =
      product !== Product.productTypes.Whole.productCodes.CPL10 &&
      product !== Product.productTypes.Whole.productCodes.CPL10A;
    return { result: isNotWL10 };
  }

  /**
   * Check if `insuredAge` is outside the age limit of 18(min) and `maxAge`/`tobaccoMaxAge`(max)
   * For WholeLife validation, get the faceAmount and validate against the slabs in the specs.
   *
   * @param age
   * @param isTobaccoUser
   * @param productSpec Product properties
   * @param term
   * @param faceAmount (WholeLife only)
   *
   * @returns true if insuredAge is greater than `maxAge`/`tobaccoMaxAge` or less than minimum age of 18
   */
  static isOutsideAgeLimit(
    dob: Date,
    isTobaccoUser: boolean,
    productSpec: object,
    term: string,
    faceAmount?: number
  ): boolean {
    if (!productSpec['termBreakdown']) {
      throw Error(`termBreakdown is not defined`);
    }
    const termSpec = productSpec['termBreakdown'][term];
    if (!termSpec) {
      throw Error(`Term ${term} is not defined for given product`);
    }

    // check min age limitation
    if (CommonUtils.isYoungerThanAge(dob, productSpec['minAge'])) {
      return true;
    }
    // check tobacco class age limitation if defined
    if (
      isTobaccoUser &&
      termSpec['tobaccoMaxAge'] &&
      CommonUtils.isOlderThanOrEqualToAge(dob, termSpec['tobaccoMaxAge'])
    ) {
      return true;
    }
    // check regular age limitation: term and whole have different specs
    if (termSpec instanceof Array) {
      // WholeLife
      if (!Utils.exists(faceAmount)) {
        throw Error('faceAmount not specified');
      }
      const faceAmountSpec = termSpec.sort((s1, s2) => s1.faceUpTo - s2.faceUpTo).find((s) => faceAmount <= s.faceUpTo);
      if (!faceAmountSpec) {
        throw Error(`Spec does not include a maxAge for the given faceAmount ${faceAmount}`);
      }
      return CommonUtils.isOlderThanOrEqualToAge(dob, faceAmountSpec['maxAge']);
    } else {
      // Term
      if (CommonUtils.isOlderThanOrEqualToAge(dob, termSpec['maxAge'])) {
        return true;
      }
    }

    return false;
  }

  private static replacements(value, options): ValidationResult {
    const splitopts = options.prop.split('.');
    const propString = splitopts.slice(splitopts.length - 1)[0];

    const existingPolicies =
      ValidationService.windowService.nativeWindow.controller.customer.application.primaryPolicy.insured
        .existingPolicies;

    if (!options.index && propString === 'replaceInforceApplication') {
      // case 1
      if (existingPolicies.replacedOrFundedPolicies.length === 0 && value === true) {
        ValidationService.addSectionError(options);
        return {
          result: true,
          error: 'At least one of the policies indicated below MUST show a status of Replaced.',
        };
      } else if (existingPolicies.replacedOrFundedPolicies.length > 0 && value === true) {
        let policyLevelReplacementAndFinanceError = false;
        existingPolicies.replacedOrFundedPolicies.forEach((policy) => {
          if (policy.isReplacement === false && policy.isReplacementFunds === false) {
            policyLevelReplacementAndFinanceError = true;
          }
        });
        if (policyLevelReplacementAndFinanceError) {
          ValidationService.addSectionError(options);
          return {
            result: true,
            error: 'At least one of the policies indicated below MUST show a status of Replaced.',
          };
        }
      }
    }

    const appLevelReplacementQuestion = existingPolicies.replaceInforceApplication;

    let existingPolicyLevelReplacementQuestion;
    let existingPolicyLevelFinanceQuestion;
    if (options.index !== undefined) {
      existingPolicyLevelReplacementQuestion = existingPolicies.existingPolicyList[options.index].isReplacement;
      existingPolicyLevelFinanceQuestion = existingPolicies.existingPolicyList[options.index].isReplacementFunds;
    }

    if (propString === 'isReplacement') {
      if (value === true && appLevelReplacementQuestion === false && existingPolicyLevelFinanceQuestion === false) {
        ValidationService.addSectionError(options);
        return {
          result: true,
          error:
            'An earlier response indicated the new policy is NOT intended to replace/change an existing policy, please review your response.',
        };
      }
      if (value === true && existingPolicyLevelFinanceQuestion === true) {
        ValidationService.addSectionError(options);
        return {
          result: true,
          error: 'Please review your response.',
        };
      }
    }

    if (propString === 'isReplacementFunds') {
      if (value === true && appLevelReplacementQuestion === false && existingPolicyLevelReplacementQuestion === false) {
        ValidationService.addSectionError(options);
        return {
          result: true,
          error:
            'An earlier response indicated the new policy is NOT intended to replace/change an existing policy, please review your response.',
        };
      }
      if (value === true && existingPolicyLevelReplacementQuestion === true) {
        ValidationService.addSectionError(options);
        return {
          result: true,
          error: 'Please review your response.',
        };
      }
    }

    ValidationService.removeSectionError(options);
    return { result: true };
  }

  private static addSectionError(question) {
    if (!question.appState.invalidSection[question.section]) {
      question.appState.invalidSection[question.section] = {
        count: 0,
        errors: [],
      };
    }
    const exists = question.appState.invalidSection[question.section].errors.indexOf(question.prop);
    if (exists === -1) {
      question.appState.invalidSection[question.section].count++;
      question.appState.invalidSection[question.section].errors.push(question.prop);
      question.appState.sectionValidationSubject.next();
    }
  }

  private static removeSectionError(question) {
    if (question.appState.invalidSection[question.section]) {
      const indexToRemove = question.appState.invalidSection[question.section].errors.indexOf(question.prop);
      if (indexToRemove !== -1) {
        question.appState.invalidSection[question.section].errors.splice(indexToRemove, 1);
        question.appState.invalidSection[question.section].count--;

        if (question.appState.invalidSection[question.section].count === 0) {
          delete question.appState.invalidSection[question.section];
        }
        question.appState.sectionValidationSubject.next();
      }
    }
  }

  public validateInsuredAgeForProduct(dob: Date, product: string): string {
    if (dob && product) {
      const { minAge, maxAge } = Product.productSpecs[product];
      const { result, error } = ValidationService.isDOBWithin(dob, minAge, maxAge);
      return result ? '' : error;
    }
  }

  private static isStandardHealthClass(value) {
    switch (value) {
      case 'STANDARD':
      case 'STANDARD_TOBACCO':
        return { result: true };

      default:
        return { result: false };
    }
  }

  private static isPreferredOrSelectClass(value) {
    if (value === null || value.length === 0) {
      return { result: false };
    }

    const isStandardClass = ValidationService.isStandardHealthClass(value);
    return { result: !isStandardClass.result };
  }

  public static isJuvenile(value: Date): boolean {
    return Utils.getActualAge(value) < 18;
  }

  public static isValidJuvenileDOB(value, maxAge?: number) {
    const today = new Date();
    const sixteenAndHalfYearsInMonths = 16 * 12 + 6;
    const dob: Date = new Date(value);
    dob.setHours(0, 0, 0, 0);

    // Initial juvenile DOB constraints are >= 15 days old and < 18 years old (WA 16.5 max)
    const minDate = Utils.offsetDate(today, -15, 'days');
    let maxDate = Utils.offsetDate(today, -sixteenAndHalfYearsInMonths, 'months');

    if (maxAge) {
      const maxAgeInMonths = maxAge * 12;
      today.setDate(today.getDate() - 1);
      maxDate = Utils.offsetDate(today, -maxAgeInMonths, 'months');
    }

    minDate.setHours(0, 0, 0, 0);
    maxDate.setHours(0, 0, 0, 0);

    if (dob <= minDate && dob > maxDate) {
      return { result: true };
    } else {
      return { result: false };
    }
  }

  private getInsuranceAge(dob: Date): number {
    // const age = moment().diff(dob, "years");
    // const insuredAge = Math.round(age);
    return ValidationService.getInsuranceAge(dob);
  }

  private isTobaccoRiskClass(healthClass: string) {
    return ['SELECT_TOBACCO', 'STANDARD_TOBACCO'].includes(healthClass);
  }

  /**
   * TODO: Convert strings into enums (HL-19494).
   */
  public static isMinimumFaceAmountMet({
    isJoint,
    productType,
    product = '',
    healthClass,
    faceAmount,
    lowFaceWholeLifeEnabled = false,
    isPolicyWLandLowFace = 'NA',
    dob,
  }: {
    isJoint?: boolean;
    productType: ProductType;
    product?: string;
    healthClass: string;
    faceAmount: number;
    lowFaceWholeLifeEnabled?: boolean;
    isPolicyWLandLowFace?: 'NA' | 'true' | 'false';
    dob?: Date;
  }): true | number {
    // we only check for errors if all required values are provided
    if (!productType || !healthClass) {
      return true;
    }

    let healthClassToUse = healthClass;
    if (productType !== ProductType.TERM && faceAmount < 50000) {
      healthClassToUse = RateClasses[healthClass].smoker === true ? 'STANDARD_TOBACCO' : 'STANDARD';
    }

    const minAllowed = ValidationStatic.getMinimumFaceAmountAllowed({
      isJoint,
      productType,
      product,
      healthClass: healthClassToUse,
      lowFaceWholeLifeEnabled,
      isPolicyWLandLowFace,
      dob,
    });
    if (faceAmount < minAllowed) {
      return minAllowed;
    }

    return true;
  }

  isMaximumFaceAmountMetForTerm({
    dob,
    faceAmount,
    maxFaceAmount,
    isUsCitizen,
  }: {
    dob: Date;
    faceAmount: number;
    maxFaceAmount: number | null;
    isUsCitizen: boolean;
  }): true | number {
    if (dob && faceAmount) {
      const maxAllowed =
        maxFaceAmount ||
        ValidationStatic.getMaximumAllowedForTerm({
          dob,
          isUsCitizen,
        });
      if (faceAmount > maxAllowed) {
        return maxAllowed;
      }
    }
    return true;
  }

  private static isValidGirAmount(value): ValidationResult {
    const customer = ValidationService.windowService.nativeWindow.controller.customer;
    const faceAmount = customer.application.primaryPolicy.faceAmount;
    const girConstants = RiderConstants.gir;
    const isValidGirAgeResult: ValidationResult = ValidationService.isValidGirAge(
      CoverpathUtils.getInsuredDob(customer.application)
    );
    if (!isValidGirAgeResult.result) {
      return isValidGirAgeResult;
    } else if (!value) {
      return { result: false, error: 'Face amount must be provided' };
    } else if (value < girConstants.minFace || value > Math.min(2 * faceAmount, girConstants.maxFace)) {
      return {
        result: false,
        error: `Guaranteed additional insurability amount must be between ${Utils.formatCurrency(
          girConstants.minFace,
          0
        )} and ${Utils.formatCurrency(Math.min(2 * faceAmount, girConstants.maxFace), 0)}`,
      };
    }

    return { result: true };
  }

  private static isValidRtrAmount(value): ValidationResult {
    const customer: CoverpathCustomer = ValidationService.windowService.nativeWindow.controller.customer;
    const faceAmount = customer.application.primaryPolicy.getTotalFaceAmount();
    const rtrConstants = RiderConstants.rtr;
    const rtrMinFaceAmount = rtrConstants.minFace;
    const policyValues = ValidationService.extractPolicyValuesForValidation(
      customer,
      'customer.application.primaryPolicy.rtrRiderAmount',
      value
    );
    const controller = ValidationService.windowService.nativeWindow.controller;
    const rtrMaxFaceAmount: number = policyValues.isUsCitizen
      ? rtrConstants.maxFace
      : rtrConstants.maxFaceForNoncitizens;
    const totalMaxFaceAmount: number = Math.min(20 * faceAmount, rtrMaxFaceAmount - faceAmount);
    const lowFaceWL10OverrideEnabled: boolean = controller.lowFaceWL10OverrideEnabled ?? false;
    const lowFaceWL10Enabled = ValidationStatic.isLowFaceWL10Enabled(
      controller.lowFaceWL10EnableDate,
      policyValues.submittedAt,
      lowFaceWL10OverrideEnabled
    );
    // validate face first since age validation depends on face.
    if (!value) {
      return { result: false, error: 'Face amount must be provided' };
    } else if (value < rtrMinFaceAmount || value > totalMaxFaceAmount) {
      let error: string;
      if (totalMaxFaceAmount > rtrMinFaceAmount) {
        error = `Renewable term face amount must be between ${Utils.formatCurrency(
          rtrMinFaceAmount,
          0
        )} and ${Utils.formatCurrency(totalMaxFaceAmount, 0)}`;
      } else if (totalMaxFaceAmount === rtrMinFaceAmount) {
        error = `Renewable term face amount must be ${Utils.formatCurrency(rtrMinFaceAmount, 0)}`;
      } else {
        let maxAllowed: number;
        if (customer.application.isJointApp()) {
          maxAllowed = JointAppValidationUtils.isValidFaceAmount(
            policyValues.primaryProductCode as WHOLE_LIFE_PRODUCT_CODE,
            policyValues.primaryFace,
            policyValues.secondaryProductCode as TERM_PRODUCT_CODE,
            policyValues.secondaryFace,
            policyValues.rtrAmount,
            policyValues.dob,
            customer.primaryQuote.healthClass as HEALTH_CLASS,
            controller.lowFaceWholeLifeEnabled,
            policyValues.isUsCitizen,
            lowFaceWL10Enabled
          ).maxAllowed;
        } else {
          maxAllowed = WholeLifeValidationUtils.isValidFaceAmount(
            policyValues.primaryProductCode as WHOLE_LIFE_PRODUCT_CODE,
            policyValues.primaryFace,
            policyValues.rtrAmount,
            policyValues.dob,
            customer.primaryQuote.healthClass as HEALTH_CLASS,
            controller.lowFaceWholeLifeEnabled,
            policyValues.isUsCitizen,
            lowFaceWL10Enabled
          ).maxAllowed;
        }
        error = `You cannot add RTR because it would put you over the allowed face amount of ${Utils.formatCurrency(
          maxAllowed,
          0
        )}.`;
      }
      return {
        result: false,
        error,
      };
    } else {
      const product = customer.application.primaryPolicy.product;
      const isValidRtrAge = ValidationStatic.isValidRtrAge(
        product,
        faceAmount,
        policyValues.rtrAmount,
        CoverpathUtils.getInsuredDob(customer.application)
      );
      if (!isValidRtrAge.result) {
        return isValidRtrAge;
      }
    }

    return { result: true };
  }

  /**
   * This method is used to retrieve all values required for scheduled & unscheduled ALIR validations.
   * Values are defaulted to their pending amendment values for code reuse post-UW.
   * Additionally, this metohd can be given the name and value of a change which hasn't yet been saved so that
   * we can determine if that change is valid before saving it.
   */
  public static async overrideForAlir(
    value,
    fieldName: string,
    primaryPolicy: CoverpathBasePolicy,
    customer: CoverpathCustomer
  ): Promise<{
    errorMsg: string;
    scheduledAlirAmountForReq: number;
    unscheduledAlirAmountForReq: number;
    wholeLifeFaceAmountForReq: number;
    jointAppFaceAmountForReq: number;
    rtrRiderAmountForReq: number;
    insuranceAgeForReq: number;
    actualAgeForReq: number;
    isUsCitizenForReq: boolean;
    wholeLifeAnnualBasePremiumForReq: number;
    productCodeForReq: string;
    paymentFrequency: string;
    maxFace: number;
  }> {
    const noPremiumError: string = 'Error validating ALIR Scheduled Payment Amount - No premium returned from service.';
    const secondaryPolicy = primaryPolicy.application.secondaryPolicy as CoverpathBasePolicy;
    const hasScheduledPayments: boolean =
      primaryPolicy.hasAlirRiderWithPendingAmendment() &&
      primaryPolicy.alirRider.hasAlirScheduledPaymentsWithPendingAmendment();
    const maxFace: number = primaryPolicy.maxFace;
    let paymentFrequency = primaryPolicy.paymentFrequencyWithPendingAmendment();
    let scheduledAlirAmountForReq = primaryPolicy.hasAlirRiderWithPendingAmendment()
      ? primaryPolicy.alirRider.amountWithPendingAmendment()
      : null;
    let unscheduledAlirAmountForReq = primaryPolicy.hasAlirRiderWithPendingAmendment()
      ? primaryPolicy.alirRider.alir1035ExchangeAmountWithPendingAmendment()
      : null;
    let jointAppFaceAmountForReq = primaryPolicy.faceAmountWithPendingAmendment();
    if (primaryPolicy.application.isJointAppWithPendingAmendment()) {
      jointAppFaceAmountForReq +=
        primaryPolicy.application.secondaryPolicyWithPendingAmendment.faceAmountWithPendingAmendment();
    }
    let wholeLifeFaceAmountForReq = primaryPolicy.faceAmountWithPendingAmendment();
    let wholeLifeAnnualBasePremiumForReq = primaryPolicy.premiumBreakdown.baseAnnual;
    let wholeLifeTotalPremium = primaryPolicy.premiumBreakdown.getTotalWithPendingAmendment();
    let wholeLifeTerm = primaryPolicy.termWithPendingAmendment();
    let insuranceAgeForReq = ValidationService.getInsuranceAge(CoverpathUtils.getInsuredDob(customer.application));
    let actualAgeForReq = ValidationService.publicGetAge(CoverpathUtils.getInsuredDob(customer.application));
    let isUsCitizenForReq = primaryPolicy.insured.person.isCitizen();
    let productCodeForReq = primaryPolicy.productWithPendingAmendment();
    let alir1035Purpose = primaryPolicy.alir1035Purpose;
    let hasWaiver: boolean = primaryPolicy.hasWaiverWithPendingAmendment();
    let hasGirRider: boolean = primaryPolicy.hasGirRiderWithPendingAmendment();
    let hasRtrRider: boolean = primaryPolicy.hasRtrRiderWithPendingAmendment();
    let girRiderAmount: number = hasGirRider ? primaryPolicy.girRider.amountWithPendingAmendment() : 0;
    let rtrRiderAmountForReq: number = primaryPolicy.hasRtrRiderWithPendingAmendment()
      ? primaryPolicy.rtrRider.amountWithPendingAmendment()
      : 0;
    let dob: Date = primaryPolicy.insured.person.dob;
    let gender = primaryPolicy.insured.person.gender;
    let errorMsg: string;

    // fetchNewPremiums should only be set to false for props which don't directly affect WL Premium values
    let fetchNewPremiums: boolean = true;

    // override for whatever was passed in
    switch (fieldName) {
      case 'cashValueTransfer':
        if (!value && primaryPolicy.alirRider && primaryPolicy.alirRider.exchange1035) {
          value = primaryPolicy.alirRider.exchange1035.cashValueTransfer;
        }
        unscheduledAlirAmountForReq = value;
        fetchNewPremiums = false;
        break;
      case 'alir1035ExchangeAmount':
      case 'alirLumpSumAmount':
        unscheduledAlirAmountForReq = value;
        fetchNewPremiums = false;
        break;
      case 'additionalLifeInsuranceAmount':
        scheduledAlirAmountForReq = value;
        break;
      case 'additionalLifeInsuranceAmountMonthly':
        scheduledAlirAmountForReq = value * 12;
        break;
      case 'primaryPolicy.faceAmount':
        wholeLifeFaceAmountForReq = value;
        jointAppFaceAmountForReq = value + (secondaryPolicy ? secondaryPolicy.faceAmount : 0);
        break;
      case 'secondaryPolicy.faceAmount':
        jointAppFaceAmountForReq = value + primaryPolicy.faceAmount;
        fetchNewPremiums = false;
        break;
      case 'paymentFrequency':
        paymentFrequency = value;
        break;
      case 'waiver':
        hasWaiver = value;
        break;
      case 'hasGirRiderBool':
        hasGirRider = value;
        break;
      case 'girRiderAmount':
        girRiderAmount = value;
        break;
      case 'hasRtrRiderBool':
        // if user un-checked RTR (value === false), need to use 0 for rtr amount
        rtrRiderAmountForReq = value ? rtrRiderAmountForReq : 0;
        hasRtrRider = value;
        break;
      case 'rtrRiderAmount':
        rtrRiderAmountForReq = value;
        break;
      case 'dob':
        insuranceAgeForReq = ValidationService.getInsuranceAge(value);
        actualAgeForReq = ValidationService.publicGetAge(value);
        dob = value;
        break;
      case 'residencyStatus':
        isUsCitizenForReq = value === 'citizen';
        fetchNewPremiums = false;
        break;
      case 'term':
        wholeLifeTerm = parseInt(value);
        productCodeForReq = getWLProductCode(wholeLifeTerm);
        if (primaryPolicy.alirPlannedYears) {
          const alirPlannedYearsValidationResult = ValidationService.isValidAlirPlannedYears(
            primaryPolicy.alirPlannedYears,
            value
          );
          if (!alirPlannedYearsValidationResult.result) {
            errorMsg = alirPlannedYearsValidationResult.error;
          }
        }
        break;
      case 'gender':
        gender = value;
        break;
      case 'alir1035Purpose':
        alir1035Purpose = value;
        fetchNewPremiums = false;
        break;
      default:
        fetchNewPremiums = false;
        break;
    }

    if (fetchNewPremiums || !wholeLifeAnnualBasePremiumForReq) {
      const girRider: Rider =
        hasGirRider && girRiderAmount > 0
          ? {
              type: RiderType.GIR,
              amount: girRiderAmount,
            }
          : null;
      const rtrRider: Rider =
        hasRtrRider && rtrRiderAmountForReq > 0
          ? {
              type: RiderType.RTR,
              amount: rtrRiderAmountForReq,
            }
          : null;
      const alirRider: Rider =
        hasScheduledPayments && scheduledAlirAmountForReq > 0
          ? {
              type: RiderType.ALIR,
              amount: scheduledAlirAmountForReq,
            }
          : null;
      const applicationState: string = primaryPolicy.getApplicationState();
      const ratingCode = ProductServiceUtils.getRateClassCodeForProductRequest(primaryPolicy);
      const wlBreakdown = await ValidationService.fetchWholeLifePremiumBreakdown(
        wholeLifeFaceAmountForReq,
        dob,
        paymentFrequency,
        gender,
        wholeLifeTerm,
        productCodeForReq,
        hasWaiver,
        girRider,
        rtrRider,
        alirRider,
        applicationState,
        ratingCode
      );
      if (wlBreakdown && wlBreakdown.annualBasePremium) {
        wholeLifeAnnualBasePremiumForReq = wlBreakdown.annualBasePremium;
        wholeLifeTotalPremium = wlBreakdown.totalPremium;
      } else if (!errorMsg) {
        errorMsg = noPremiumError;
      }
    }

    if (alir1035Purpose === Exchange1035FundingPurpose.PREMIUM_AND_ALIR) {
      if (unscheduledAlirAmountForReq <= wholeLifeTotalPremium) {
        unscheduledAlirAmountForReq = null;
      } else {
        unscheduledAlirAmountForReq -= wholeLifeTotalPremium;
      }
    }

    return {
      errorMsg,
      scheduledAlirAmountForReq,
      unscheduledAlirAmountForReq,
      jointAppFaceAmountForReq,
      wholeLifeFaceAmountForReq,
      wholeLifeAnnualBasePremiumForReq,
      rtrRiderAmountForReq,
      insuranceAgeForReq,
      actualAgeForReq,
      isUsCitizenForReq,
      paymentFrequency,
      productCodeForReq,
      maxFace,
    };
  }

  /**
   * This method is to generate a whole life premium breakdown using entirely transient values so that we
   * can calculate the cost of a hypothetical scenario without affecting our saved policy values.
   */
  private static async fetchWholeLifePremiumBreakdown(
    faceAmount: number,
    dob: Date,
    paymentFrequency: string,
    gender: '1' | '2',
    term: number,
    productCode: string,
    hasWaiver: boolean,
    girRider: Rider,
    rtrRider: Rider,
    alirRider: Rider,
    applicationState: string,
    ratingCode: string
  ): Promise<WholeLifeQuotePremiumBreakdown> {
    const riders: Rider[] = [];
    if (rtrRider) {
      riders.push(rtrRider);
    }
    if (girRider) {
      riders.push(girRider);
    }

    try {
      const productResponse: ProductQuoteResponse = await ValidationService.getPremium(
        faceAmount,
        dob,
        paymentFrequency,
        gender,
        term,
        productCode,
        'Whole',
        ratingCode,
        applicationState,
        hasWaiver,
        riders
      );
      const girResponseRider = productResponse.riders.find((rider) => rider.type === RiderType.GIR);
      const rtrResponseRider = productResponse.riders.find((rider) => rider.type === RiderType.RTR);
      const breakdown = {
        paymentFrequency,
        basePremium: productResponse.base,
        annualBasePremium: productResponse.annualBase,
        hasWaiver: productResponse.waiver > 0,
        waiver: productResponse.waiver,
        hasGirRider: !!girResponseRider,
        girPremium: girResponseRider ? girResponseRider.premium : null,
        girWaiver: girResponseRider ? girResponseRider.waiver : null,
        hasRtrRider: !!rtrResponseRider,
        rtrPremium: rtrResponseRider ? rtrResponseRider.premium : null,
        rtrWaiver: rtrResponseRider ? rtrResponseRider.waiver : null,
        alirEnabled: !!alirRider, // ALIR data generated in Coverpath, not Product
        alirAmount: alirRider ? alirRider.amount : null,
      } as WholeLifeQuotePremiumBreakdown;
      // FlatExtras excluded from request
      return PremiumCalculationService.calculateWholeLifePremium(breakdown);
    } catch (e) {
      return null;
    }
  }

  private static getPolicyPremiumAsAnnual(paymentFrequency: string, value: number): number {
    return paymentFrequency === 'annual' ? value : value * 12;
  }

  public static validateCirAgeLimit(value) {
    const controller = ValidationService.windowService.nativeWindow.controller;
    const primaryPolicy: CoverpathPolicy = controller.customer.application.primaryPolicy;
    if (!primaryPolicy.hasCirRider) {
      return { result: true };
    }

    const ageValidateResult = CIRValidationUtils.isValidCirAge(value);
    if (!ageValidateResult.isValid) {
      return {
        result: false,
        error: ageValidateResult.message,
      };
    } else {
      return { result: true };
    }
  }

  public static validateCirAmount(value, newFaceAmt?) {
    const controller = ValidationService.windowService.nativeWindow.controller;
    const customer = controller.customer;
    const primaryPolicy: CoverpathPolicy = customer.application.primaryPolicy;

    if (!primaryPolicy.hasCirRider) {
      return { result: true };
    }

    const faceAmount = newFaceAmt || primaryPolicy.faceAmount;
    const cirAmount = value;
    const cirAmountValidateResult = CIRValidationUtils.isValidCirBaseBenefitPool(faceAmount, cirAmount);

    if (!cirAmountValidateResult.isValid) {
      return {
        result: false,
        error: cirAmountValidateResult.message,
      };
    } else {
      return { result: true };
    }
  }

  public static async validateAlirAmountGeneric(value, fieldName: string) {
    const controller = ValidationService.windowService.nativeWindow.controller;
    const customer = controller.customer;
    const primaryPolicy: CoverpathBasePolicy = customer.application.primaryPolicy;

    if (!primaryPolicy.hasAlirRider && fieldName !== 'cashValueTransfer') {
      /**
       * ALIR is not selected in the application
       * no need to validate ALIR Scheduled Amount
       */
      return { result: true };
    }
    if (fieldName === 'cashValueTransfer' && !value) {
      // Don't validate replacements without cash value. For both WL & Term apps.
      return { result: true };
    } else if (primaryPolicy.isTermLifePolicy()) {
      return { result: false }; // Must be applying for WL to have a 1035
    }
    const values = await ValidationService.overrideForAlir(value, fieldName, primaryPolicy, customer);
    const isAdvisor = this.windowService.nativeWindow.amorphic.app === 'coverpath-agent';
    if (values.errorMsg) {
      return {
        result: false,
        error: values.errorMsg,
        unsupportedCaseData: isAdvisor ? values.errorMsg : null,
        unsupportedCase: UnsupportedCase.alirCompoundValidation,
      };
    }
    const validationResult = WholeLifeValidationUtils.isValidAlirAmount(
      values.scheduledAlirAmountForReq,
      values.unscheduledAlirAmountForReq,
      values.jointAppFaceAmountForReq,
      values.rtrRiderAmountForReq,
      values.insuranceAgeForReq,
      values.actualAgeForReq,
      values.isUsCitizenForReq,
      values.wholeLifeAnnualBasePremiumForReq,
      values.productCodeForReq,
      values.paymentFrequency,
      values.maxFace
    );

    if (!validationResult.isValid) {
      return {
        result: false,
        error: validationResult.message,
        unsupportedCaseData: isAdvisor ? validationResult.message : null,
        unsupportedCase: UnsupportedCase.alirCompoundValidation,
      };
    }
    return { result: true };
  }

  private static async getPremium(
    faceAmount: number,
    dob: Date,
    paymentFrequency: string,
    gender: '1' | '2',
    term: number,
    product: string,
    productType: string,
    ratingCode: string,
    state: string,
    hasWaiver: boolean,
    riders: Rider[]
  ): Promise<ProductQuoteResponse> {
    const request: PremiumRequest = {
      dob: dob,
      product: product,
      productType: productType,
      effectiveDate: Utils.now(),
      gender: gender,
      ratingCode: ratingCode,
      state: state,
      term: term,
      face: faceAmount,
      flatExtras: [],
      paymentFrequency: paymentFrequency,
      waiver: hasWaiver,
      riders: riders,
    };

    const productResponse: ProductQuoteResponse =
      await ValidationService.windowService.nativeWindow.controller.publicFetchProductQuote(request);
    return productResponse;
  }

  private async isValidAlirUnscheduledAmount(value, fieldName: string): Promise<ValidationResult> {
    /** Validation is not currently running. It will be added in HL-33246 **/
    return { result: true };
    /** Validation is not currently running. It will be added in HL-33246 **/
    if (!value && value !== 0) {
      return {
        result: false,
        error: 'Amount must be provided',
      };
    }

    const controller = ValidationService.windowService.nativeWindow.controller;
    const customer = controller.customer;
    const primaryPolicy: CoverpathBasePolicy = customer.application.primaryPolicy;
    if (!primaryPolicy.hasAlirRider) {
      return { result: true };
    }
    const values = await ValidationService.overrideForAlir(value, fieldName, primaryPolicy, customer);

    if (values.errorMsg) {
      return {
        result: false,
        error: values.errorMsg,
      };
    }
    return ValidationService.validateAlirAmountGeneric(value, 'alirLumpSumAmount');
  }

  public static isValidAlirPlannedYears(value, years?: number, dateOfBirth?: Date): ValidationResult {
    const application = ValidationService.windowService
      ? ValidationService.windowService.nativeWindow.controller.customer.application
      : null;
    const primaryPolicy = application && application.primaryPolicy;
    const termYears = years || primaryPolicy.term;
    const dob = dateOfBirth || primaryPolicy.insured.person.dob;
    const insuranceAge: number = ValidationService.getInsuranceAge(dob);
    const validationResult = WholeLifeValidationUtils.isValidAlirPlannedYears(value, termYears, insuranceAge);
    if (!validationResult.isValid) {
      return {
        result: false,
        error: validationResult.message,
      };
    }
    return { result: true };
  }

  private static extractPolicyValuesForValidation(customer: CoverpathCustomer, property: string, value) {
    const person: Person = customer.application.primaryPolicy.insured.person;
    let dob = person.dob;

    if (!dob) {
      dob = customer.primaryQuote.dateOfBirth;
    }

    const isUsCitizen: boolean = !Utils.exists(person.residencyStatus) || person.isCitizen();
    let primaryFace = customer.application.primaryPolicy.faceAmount;
    let primaryProductCode = customer.application.primaryPolicy.product;
    let primaryTerm = customer.application.primaryPolicy.term;
    let primaryPaymentFrequency = customer.application.primaryPolicy.paymentFrequency;
    let rtrAmount = customer.application.primaryPolicy.rtrRiderAmount;
    let primaryDividendOption;

    if (customer.application.primaryPolicy.isWholeLifePolicy()) {
      primaryDividendOption = (customer.application.primaryPolicy as CoverpathPolicy).dividendOption;
    }

    let secondaryFace = customer.application.secondaryPolicy ? customer.application.secondaryPolicy.faceAmount : 0;
    let secondaryProductCode = customer.application.secondaryPolicy
      ? customer.application.secondaryPolicy.product
      : undefined;
    let secondaryTerm = customer.application.secondaryPolicy ? customer.application.secondaryPolicy.term : undefined;

    const smoker = CommonUtils.isSmokerHealthClassFromPolicy(customer.application.primaryPolicy);
    const state = customer.application.primaryPolicy.insured.address.state;
    const submittedAt = customer.application.primaryPolicy.submittedAt;

    //apply overrides
    switch (property) {
      case 'customer.application.primaryPolicy.insured.person.dob':
        dob = value;
        break;
      case 'customer.application.primaryPolicy.faceAmount':
        primaryFace = value;
        break;
      case 'customer.application.secondaryPolicy.faceAmount':
        secondaryFace = value;
        break;
      case 'customer.application.primaryPolicy.term':
      case 'customer.application.primaryPolicy.termDisplay':
        primaryTerm = value;
        if (customer.application.primaryPolicy.isWholeLifePolicy()) {
          primaryProductCode = getWLProductCode(parseInt(value));
        }

        //if setting to ART, make sure product isn't ECP
        if (
          customer.application.primaryPolicy.isTermLifePolicy() &&
          value === 1 &&
          primaryProductCode === 'CASTermECP'
        ) {
          primaryProductCode = 'CASTerm';
        }
        break;

      case 'customer.application.secondaryPolicy.term':
      case 'customer.application.secondaryPolicy.termDisplay':
        secondaryTerm = value;

        if (value === 1 && primaryProductCode === 'CASTermECP') {
          secondaryProductCode = 'CASTerm';
        }
        break;

      case 'customer.application.primaryPolicy.hasEcp':
        //toggle term product code to reflect ECP status
        if (value && primaryProductCode === 'CASTerm') {
          primaryProductCode = 'CASTermECP';
        } else if (!value && primaryProductCode === 'CASTermECP') {
          primaryProductCode = 'CASTerm';
        }
        break;

      case 'customer.application.secondaryPolicy.hasEcp':
        //toggle term product code to reflect ECP status
        if (value && secondaryProductCode === 'CASTerm') {
          secondaryProductCode = 'CASTermECP';
        } else if (!value && secondaryProductCode === 'CASTermECP') {
          secondaryProductCode = 'CASTerm';
        }
        break;

      case 'customer.application.primaryPolicy.paymentFrequency':
        primaryPaymentFrequency = value;
        break;

      case 'customer.application.primaryPolicy.dividendOption':
        primaryDividendOption = value;
        break;
      case 'customer.application.primaryPolicy.rtrRiderAmount':
        rtrAmount = value;
        break;
    }

    /*
      Coerce dob (back) to a value entered by the user

      DOBs Date objects are created without a specific time which can cause days to shift by one once
      the local timezone is applied.

      For example, if a user enters 5/26/1989, a date object will be created but, if the user is on the East cost,
      will be interpreted as May 25th, 1989 8pm Eastern.

      This is causes confusion, so we make sure the dob is 5/26/1989 in the local timezone
     */
    if (dob) {
      dob = new Date(dob.getUTCFullYear(), dob.getUTCMonth(), dob.getUTCDate());
    }

    return {
      dob,
      primaryFace,
      secondaryFace,
      primaryProductCode,
      secondaryProductCode,
      primaryTerm,
      secondaryTerm,
      primaryPaymentFrequency,
      primaryDividendOption,
      smoker,
      state,
      rtrAmount,
      isUsCitizen,
      submittedAt,
    };
  }

  public policyHasValidDOBForProduct(customer: CoverpathCustomer, property: string, value): ValidationResult {
    const policyValues = ValidationService.extractPolicyValuesForValidation(customer, property, value);

    let primaryValidationResult;

    if (customer.application.primaryPolicy.isTermLifePolicy()) {
      primaryValidationResult = TermLifeValidationUtils.isValidDOBForProduct(
        policyValues.primaryProductCode as TERM_PRODUCT_CODE,
        policyValues.dob
      );
    } else {
      primaryValidationResult = WholeLifeValidationUtils.isValidDOBForProduct(
        policyValues.primaryProductCode as WHOLE_LIFE_PRODUCT_CODE,
        policyValues.dob,
        policyValues.state
      );
    }

    if (!primaryValidationResult.isValid) {
      return {
        result: false,
        error: primaryValidationResult.message,
      };
    }

    if (!customer.application.secondaryPolicy) {
      return { result: true };
    }

    const secondaryValidationResult = TermLifeValidationUtils.isValidDOBForProduct(
      policyValues.secondaryProductCode as TERM_PRODUCT_CODE,
      policyValues.dob
    );

    if (!secondaryValidationResult.isValid) {
      return {
        result: false,
        error: secondaryValidationResult.message,
      };
    } else {
      return { result: true };
    }
  }

  public policyHasValidFaceAmount(customer: CoverpathCustomer, property: string, value): ValidationResult {
    const policyValues = ValidationService.extractPolicyValuesForValidation(customer, property, value);
    const controller = ValidationService.windowService.nativeWindow.controller;
    const lowFaceWL10OverrideEnabled: boolean = controller.lowFaceWL10OverrideEnabled ?? false;
    const lowFaceWL10Enabled = ValidationStatic.isLowFaceWL10Enabled(
      controller.lowFaceWL10EnableDate,
      policyValues.submittedAt,
      lowFaceWL10OverrideEnabled
    );

    if (!policyValues.dob) {
      return { result: false, error: 'Date of birth is missing' };
    }

    const isEditingFaceAmount =
      property === 'customer.application.primaryPolicy.faceAmount' ||
      property === 'customer.application.secondaryPolicy.faceAmount';

    let healthClassToUse = customer.primaryQuote.healthClass;
    if (
      !customer.application.primaryPolicy.isTermLifePolicy() &&
      policyValues.primaryProductCode === Product.products.CPL10A.code &&
      policyValues.primaryFace < 50000
    ) {
      healthClassToUse =
        RateClasses[customer.primaryQuote.healthClass].smoker === true ? 'STANDARD_TOBACCO' : 'STANDARD';
    }

    if (customer.application.isJointApp()) {
      return this.buildFaceAmountValidationResult(
        JointAppValidationUtils.isValidFaceAmount(
          policyValues.primaryProductCode as WHOLE_LIFE_PRODUCT_CODE,
          policyValues.primaryFace,
          policyValues.secondaryProductCode as TERM_PRODUCT_CODE,
          policyValues.secondaryFace,
          policyValues.rtrAmount,
          policyValues.dob,
          healthClassToUse as HEALTH_CLASS,
          controller.lowFaceWholeLifeEnabled,
          policyValues.isUsCitizen,
          lowFaceWL10Enabled
        ),
        isEditingFaceAmount
      );
    } else if (customer.application.primaryPolicy.isTermLifePolicy()) {
      return this.buildFaceAmountValidationResult(
        TermLifeValidationUtils.isValidFaceAmount(
          policyValues.primaryProductCode as TERM_PRODUCT_CODE,
          policyValues.primaryFace,
          policyValues.dob,
          policyValues.isUsCitizen
        ),
        isEditingFaceAmount
      );
    } else {
      return this.buildFaceAmountValidationResult(
        WholeLifeValidationUtils.isValidFaceAmount(
          policyValues.primaryProductCode as WHOLE_LIFE_PRODUCT_CODE,
          policyValues.primaryFace,
          policyValues.rtrAmount,
          policyValues.dob,
          healthClassToUse as HEALTH_CLASS,
          controller.lowFaceWholeLifeEnabled,
          policyValues.isUsCitizen,
          lowFaceWL10Enabled
        ),
        isEditingFaceAmount
      );
    }
  }

  private buildFaceAmountValidationResult(
    validationUtilsResult: { isValid: boolean; minAllowed?: number; maxAllowed?: number; message?: string },
    isEditingFaceAmount: boolean
  ): ValidationResult {
    if (validationUtilsResult.isValid) {
      return { result: true };
    }

    let message;

    if (validationUtilsResult.minAllowed) {
      message = `Face amount must be at least ${Utils.formatIntCurrency(validationUtilsResult.minAllowed.toFixed(0))}`;
    } else if (validationUtilsResult.maxAllowed) {
      message = `Total face amount cannot exceed ${Utils.formatIntCurrency(
        validationUtilsResult.maxAllowed.toFixed(0)
      )}`;
    } else {
      message = validationUtilsResult.message;
    }

    if (!isEditingFaceAmount) {
      message = `Your response was not saved: ${message}`;
    }

    return { result: false, error: message };
  }

  public policyHasValidTerm(customer: CoverpathCustomer, property: string, value): ValidationResult {
    const policyValues = ValidationService.extractPolicyValuesForValidation(customer, property, value);

    let validationResult;
    if (customer.application.primaryPolicy.isTermLifePolicy()) {
      validationResult = TermLifeValidationUtils.isValidTerm(
        policyValues.primaryProductCode as TERM_PRODUCT_CODE,
        policyValues.primaryTerm,
        policyValues.dob,
        policyValues.smoker
      );
    } else if (customer.application.isJointApp()) {
      validationResult = TermLifeValidationUtils.isValidTerm(
        policyValues.secondaryProductCode as TERM_PRODUCT_CODE,
        policyValues.secondaryTerm,
        policyValues.dob,
        policyValues.smoker
      );
    } else {
      return { result: true };
    }

    if (validationResult && validationResult.isValid) {
      return { result: true };
    } else {
      const isEditingTerm =
        property === 'customer.application.primaryPolicy.term' ||
        property === 'customer.application.primaryPolicy.termDisplay' ||
        property === 'customer.application.secondaryPolicy.term' ||
        property === 'customer.application.secondaryPolicy.termDisplay';

      const errorMessage = isEditingTerm
        ? validationResult.message
        : `Your response was not saved: ${validationResult.message}`;

      return {
        result: false,
        error: errorMessage,
      };
    }
  }

  public policyHasValidDividendOption(customer: CoverpathCustomer, property: string, value): ValidationResult {
    if (!customer.application.primaryPolicy.isWholeLifePolicy()) {
      return { result: true };
    }

    const policyValues = ValidationService.extractPolicyValuesForValidation(customer, property, value);

    const validationResult = WholeLifeValidationUtils.isValidDividendOption(
      policyValues.primaryProductCode as WHOLE_LIFE_PRODUCT_CODE,
      policyValues.primaryDividendOption as DIVIDEND_OPTION,
      CoverpathUtils.makePaymentFrequencySafeForPolicyStrict(policyValues.primaryPaymentFrequency)
    );

    if (validationResult.isValid) {
      return { result: true };
    } else {
      return {
        result: false,
        error: validationResult.message,
      };
    }
  }

  public termQuoteHasValidTerm(quote: ClientBaseQuote, property: string, value): ValidationResult {
    //sanity check that the property passed in represents the term
    if (property.toUpperCase().indexOf('TERM') === -1) {
      return {
        result: false,
        error: `Cannot run quoteHasValidTerm with property ${property}`,
      };
    }

    const term = value as number;
    const isSmoker = quote.smoker;
    const productCode = quote.product as TERM_PRODUCT_CODE;

    let validationResult;
    if (!!term && !!quote.dateOfBirth && isSmoker !== undefined) {
      validationResult = TermLifeValidationUtils.isValidTerm(productCode, term, quote.dateOfBirth, isSmoker);
    }

    if (validationResult && validationResult.isValid) {
      return { result: true };
    } else {
      return {
        result: false,
        error: validationResult && validationResult.message ? validationResult.message : 'Could not validate term.',
      };
    }
  }

  public termQuoteHasValidFaceAmount(quote: ClientBaseQuote, property: string, value): ValidationResult {
    //sanity check that the property passed in represents the faceAmount
    if (property.toUpperCase().indexOf('FACE') === -1) {
      return {
        result: false,
        error: `Cannot run termQuoteHasValidFaceAmount with property ${property}`,
      };
    }

    const faceForPrimary = value as number;

    const wholeLifeQuote = ValidationService.windowService.nativeWindow.controller.customer.wholeLifeQuote;
    const faceForSecondary = wholeLifeQuote && wholeLifeQuote.faceAmount ? wholeLifeQuote.faceAmount : 0;
    const totalFace = faceForPrimary + faceForSecondary;
    const productCode = quote.product as TERM_PRODUCT_CODE;

    let validationResult;
    if (!!quote.dateOfBirth) {
      // assume US Citizen = true because term quote does not have an input for this yet
      const isUsCitizen: boolean = true;
      validationResult = TermLifeValidationUtils.isValidFaceAmount(
        productCode,
        totalFace,
        quote.dateOfBirth,
        isUsCitizen
      );
    }

    if (validationResult && validationResult.isValid) {
      return { result: true };
    } else {
      return {
        result: false,
        error: validationResult && validationResult.message ? validationResult.message : 'Could not validate amount',
      };
    }
  }

  public static isValidGirAge(dateOfBirth: Date): ValidationResult {
    return ValidationUtils.isValidGirAge(dateOfBirth);
  }

  private static notOwnerEmailWhenOwnerDifferentThanInsured(email: string): ValidationResult {
    const policy = this.windowService.nativeWindow.controller.customer.application.primaryPolicy;
    if (policy.isOwnerSameAsInsured()) {
      return { result: true };
    }
    const ownerEmail = policy.ownerPerson && policy.ownerPerson.email ? policy.ownerPerson.email : null;
    if (ownerEmail && ownerEmail.toLowerCase() === email.toLowerCase()) {
      return { result: false, error: "This can't be the same as the owner's email" };
    } else {
      return { result: true };
    }
  }

  private static notInsuredEmailWhenOwnerDifferentThanInsured(email: string): ValidationResult {
    const policy = this.windowService.nativeWindow.controller.customer.application.primaryPolicy;
    if (policy.isOwnerSameAsInsured() || (policy.insuredIsJuvenile() && policy.isOwnerSameAsSigner())) {
      return { result: true };
    }
    const insuredEmail = policy.insured.person.email;
    if (insuredEmail && insuredEmail.toLowerCase() === email.toLowerCase()) {
      return { result: false, error: "This can't be the same as the insured's email" };
    } else {
      return { result: true };
    }
  }

  private static notOwnerEmailWhenOwnerDifferentThanSigner(email: string): ValidationResult {
    const policy = this.windowService.nativeWindow.controller.customer.application.primaryPolicy;
    if (policy.isOwnerSameAsSigner()) {
      return { result: true };
    }
    const ownerEmail = policy.ownerPerson && policy.ownerPerson.email;
    if (ownerEmail && ownerEmail.toLowerCase() === email.toLowerCase()) {
      return { result: false, error: "This can't be the same as the owner's email" };
    }
    return { result: true };
  }

  private static notSignerEmailWhenSignerDifferentThanOwner(email: string): ValidationResult {
    const policy = this.windowService.nativeWindow.controller.customer.application.primaryPolicy;
    if (policy.isOwnerSameAsSigner()) {
      return { result: true };
    }
    const signerEmail = policy.insured.signer.email;
    if (signerEmail && signerEmail.toLowerCase() === email.toLowerCase()) {
      return { result: false, error: "This can't be the same as the signer's email" };
    }
    return { result: true };
  }

  public static requiresOwnerAcknowledgement(policy: Policy): boolean {
    // If owner is insured, then no need to acknowledge twice
    return !policy.isOwnerSameAsInsured() && !this.isCPAndAgentSubmittedApp(policy);
  }

  private static isCPAndAgentSubmittedApp(policy) {
    return policy.isCoverpathPolicy() && (policy.application as CasApplication).isAgentSubmittedApp;
  }

  public static requiresJointOwnerAcknowledgement(policy: Policy): boolean {
    // when policy.isJointOwnerSomeoneElse is 'false' means the joint owner is insured
    return (
      policy.isJointOwnership() && policy.isJointOwnerSomeoneElse !== 'false' && !this.isCPAndAgentSubmittedApp(policy)
    );
  }

  public static juviNoPCP(value: boolean): ValidationResult {
    let warning;
    const policy = this.windowService.nativeWindow.controller.customer.application.primaryPolicy;
    if (!value && policy.insuredIsJuvenile()) {
      const insuredName = policy.insured.person.firstName ? policy.insured.person.firstName : 'the insured';
      warning = `Your policy might take longer to review if a primary physician is not provided.
      If ${insuredName} sees multiple physicians you can tell us the most recent one.`;
    }
    return { result: true, warning };
  }

  public static juviUnknownLastPCPVisitDate(value: string): ValidationResult {
    let warning;
    const policy = this.windowService.nativeWindow.controller.customer.application.primaryPolicy;
    if (value === 'unknown' && policy.insuredIsJuvenile()) {
      warning = 'Your policy might take longer to review if date of last visit is not provided.';
    }
    return { result: true, warning };
  }

  public static shortDateIsAfterInsuredDOB(value: Date): ValidationResult {
    const policy = ValidationService.getPrimaryPolicy();
    const dob = policy.insured.person.dob;
    if (value && value.getUTCFullYear && dob) {
      const birthYear = dob.getUTCFullYear();
      const givenYear = value.getUTCFullYear();
      if (givenYear < birthYear || (givenYear === birthYear && value.getUTCMonth() < dob.getUTCMonth())) {
        return { result: false, error: "This can't be earlier than the insured's date of birth" };
      }
    }
    return { result: true };
  }

  public static getPrimaryPolicy(): Policy {
    return ValidationService.windowService.nativeWindow.controller.customer.application.primaryPolicy;
  }

  // Ensure that if they add an ALIR rider that at least one reflexive is answered "Yes"
  public static isValidAlirTypeSelection(policy: CoverpathBasePolicy, prop: string, value: boolean): boolean {
    let { hasAlirScheduledPayments, alirLumpSumUses1035, hasAlirLumpSum } = policy;
    switch (prop) {
      case 'hasAlirScheduledPayments':
        hasAlirScheduledPayments = value;
        break;
      case 'alirLumpSumUses1035':
        alirLumpSumUses1035 = value;
        break;
      case 'hasAlirLumpSum':
        hasAlirLumpSum = value;
        break;
    }
    // undefined means the user hasn't answered the question yet, which is valid
    return !(hasAlirScheduledPayments === false && alirLumpSumUses1035 === false && hasAlirLumpSum === false);
  }

  public static isAccountRoutingInfoSame(paymentInfo): boolean {
    return !!(paymentInfo.paymentABA && paymentInfo.paymentABA === paymentInfo.paymentAccount);
  }

  public static async validateBankAccountInfo(value, fieldName): Promise<ValidationResult> {
    const controller = ValidationService.windowService.nativeWindow.controller;
    const policy: CoverpathBasePolicy = controller.customer.application.primaryPolicy as CoverpathBasePolicy;

    const paymentAccount: string = fieldName === 'paymentAccount' ? value : policy.paymentAccount;
    const paymentABA: string = fieldName === 'paymentABA' ? value : policy.paymentABA;
    const paymentInfoCombo: string = `${paymentAccount}${paymentABA}`;

    // make sure paymentAcc and paymentABA are ok first, and not already checked
    if (
      this.isMinLength(paymentABA, 9).result &&
      this.isMaxLength(paymentABA, 9).result &&
      this.isMaxLength(paymentAccount, 17).result &&
      (!policy.lastWebDebitAttemptCombination || policy.lastWebDebitAttemptCombination !== paymentInfoCombo) &&
      paymentABA !== paymentAccount
    ) {
      policy.lastWebDebitAttemptCombination = paymentInfoCombo;
      const webDebitResults = await this.verifyWebDebit({ paymentABA: paymentABA, paymentAccount: paymentAccount });
      policy.giactWebDebitResponseCode = webDebitResults.normalizedResponseCode;
      policy.isGiactWebDebitValid = webDebitResults.status === 'pass';
      controller.publicSave();
    }
    return { result: true };
  }

  public static verifyIsNewPaymentCombination(paymentInfoCombo: string, overrideOldPaymentInfoCombo?: string) {
    const controller = ValidationService.windowService.nativeWindow.controller;
    const policy: CoverpathBasePolicy = controller.customer.application.primaryPolicy as CoverpathBasePolicy;
    const isNewBankInfo: boolean =
      !policy.lastWebDebitAttemptCombination ||
      policy.lastWebDebitAttemptCombination !== paymentInfoCombo ||
      (!!overrideOldPaymentInfoCombo && overrideOldPaymentInfoCombo !== paymentInfoCombo);
    if (isNewBankInfo && _.isNil(overrideOldPaymentInfoCombo)) {
      controller.giactController.updateLastWebDebitAttemptCombination(policy, paymentInfoCombo);
    }
    return isNewBankInfo;
  }

  public static async verifyWebDebit(newValues): Promise<GiactWebDebitResponse> {
    const controller = ValidationService.windowService.nativeWindow.controller;
    const policy = controller.customer.application.primaryPolicy;
    const giactLdFlagValue = controller.mmGiactWebDebitVerificationServiceEnabled;
    const currentUser = GiactUtils.getLoggedInUser(ValidationService.windowService.nativeWindow);
    GiactUtils.setGiactValidatingStatus(true);
    const webDebitResults = await controller.giactController.verifyGiactWebDebit(
      policy,
      giactLdFlagValue,
      currentUser,
      newValues
    );
    GiactUtils.setGiactValidatingStatus(false);
    return webDebitResults;
  }
}
